import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { format_amount, isFutureDate } from "../../../helpers/helpers";
import FormProcessingSpinner from "../../common/FormProcessingSpinner";
import CustomPopup from "../../../states/actions/CancelPopup";
import PausePopup from "../../../states/actions/PausePopup";
import { resumeSubscription } from "../../../states/actions/pauseSubscription";
import { TOAST_SETTINGS } from "../../../utils/siteSettings";

// Format an ISO date (YYYY-MM-DD) as "Month DD, YYYY". Falls back to the
// raw value if it can't be parsed so we never show "Invalid Date".
const formatLongDate = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const isRootUser = (member) =>
  member?.super_admin === null ||
  member?.super_admin === "" ||
  member?.super_admin === undefined ||
  member?.super_admin === 0;

const MembershipManagement = ({
  subscription,
  site_settings,
  member,
  handleCancelSubscription,
  isFormProcessing,
}) => {
  const dispatch = useDispatch();
  const [show, setShow] = useState(false);
  const [showPause, setShowPause] = useState(false);
  const [resuming, setResuming] = useState(false);

  const handleToggle = () => setShow((prev) => !prev);
  const togglePause = () => setShowPause((prev) => !prev);

  const is_Expired = isFutureDate(subscription?.end_date);

  const isPaused = Number(member?.mem_pause_status) === 1;
  const hasActiveSubscription =
    !!subscription &&
    subscription.id > 0 &&
    Number(subscription?.subscription_status) === 1;
  const rootUser = isRootUser(member);
  // Pause fee comes from the API exclusively — no hardcoded fallback. If
  // the backend omits it for any reason the fee phrase is dropped from the
  // copy rather than displayed with a default value that may be wrong.
  const pauseFee = member?.mem_pause_fee;
  const accessUntil = member?.mem_pause_access_until || subscription?.expiry_date;

  const handleResume = async () => {
    if (resuming) return;
    setResuming(true);
    try {
      const data = await dispatch(resumeSubscription());
      if (data?.status === 1) {
        toast.success(data?.msg || "Subscription resumed.", TOAST_SETTINGS);
        if (data?.redirect_to_pricing) {
          setTimeout(() => {
            window.location.href = "/pricing";
          }, 600);
        }
      } else {
        toast.error(
          data?.msg || "Unable to resume subscription.",
          TOAST_SETTINGS
        );
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.msg ||
          err?.message ||
          "Unable to resume subscription.",
        TOAST_SETTINGS
      );
    } finally {
      setResuming(false);
    }
  };

  return (
    <>
      {rootUser ? (
        <div className="setting_blk wide_md">
          <h4>Membership management</h4>
          <div className="mini_text_sm">
            Contact Sales at{" "}
            <Link to={"tel:" + site_settings?.site_phone}>
              {site_settings?.site_phone}
            </Link>{" "}
            or{" "}
            <Link to={"mailto:" + site_settings?.site_email}>
              {site_settings?.site_email}
            </Link>
          </div>

          <div className="table_blk">
            <div className="inner_tbl">
              <table>
                <thead>
                  <tr>
                    <th>User</th>
                    <th>License</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      <div className="mini_color_text">
                        {member?.mem_fname + " " + member?.mem_lname} (
                        {member?.mem_email})
                      </div>
                    </td>
                    <td>{subscription?.plan_name} </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : (
        ""
      )}
      <div className="pkg_upgrade_blk pro_upgrade_blk">
        <div className="pkg_badge">
          <img src="/images/green-star.svg" alt="" />
          <span>{subscription?.plan_name}</span>
        </div>
        <div className="cntnt">
          <p>
            {format_amount(subscription?.price)} /{" "}
            {subscription?.plan_type === "monthly"
              ? "mo"
              : subscription?.plan_type === "yearly"
              ? "yr"
              : ""}
          </p>
          <div className="price_body">
            <ul>
              <li>
                {parseInt(subscription?.clicks_count)} <span>clicks</span>
              </li>
              <li>
                {parseInt(subscription?.downloads_count)} <span>downloads</span>
              </li>
              <li>
                {parseInt(subscription?.user_count)} <span>User</span>
              </li>
            </ul>
          </div>
          <p>
            <small>
              {subscription?.subscription_status === 1
                ? "Renews"
                : subscription?.subscription_status === 0
                ? is_Expired
                  ? "Expires"
                  : "Expired"
                : "Renews"}{" "}
              on {subscription?.expiry_date}
            </small>
          </p>
          {subscription?.is_trial === 1 &&
          subscription?.subscription_status === 1 ? (
            <p className="mt-20">
              <small>The trial will end in two days.</small>
            </p>
          ) : (
            ""
          )}
        </div>
        {rootUser && isPaused ? (
          <div className="card_actions">
            <p className="card_action_hint">
              <strong>Subscription paused.</strong> Your data is safe
              {pauseFee != null && (
                <>
                  {" "}
                  — holding fee <strong>${pauseFee}/month</strong>
                </>
              )}
              .
              {accessUntil && (
                <>
                  {" "}
                  Access was active until{" "}
                  <strong>{formatLongDate(accessUntil)}</strong>.
                </>
              )}
            </p>
            <button
              type="button"
              className="site_btn"
              onClick={handleResume}
              disabled={resuming}
            >
              {resuming ? "Resuming…" : "Resume Subscription"}
            </button>
            {hasActiveSubscription ? (
              <Link
                to="#!"
                className="site_btn cancel_subscription_btn"
                onClick={handleToggle}
              >
                Cancel Subscription
                <FormProcessingSpinner isFormProcessing={isFormProcessing} />
              </Link>
            ) : (
              ""
            )}
          </div>
        ) : rootUser && hasActiveSubscription ? (
          <div className="card_actions">
            <Link to="/pricing" className="site_btn">
              Upgrade Now
            </Link>
            <button
              type="button"
              className="site_btn ghost_btn"
              onClick={togglePause}
            >
              Pause Subscription
            </button>
            <p className="card_action_hint">
              Pausing keeps all your data safe
              {pauseFee != null && (
                <>
                  {" "}
                  for a small <strong>${pauseFee}/month</strong> holding fee
                </>
              )}
              . You won't be charged until your renewal date.
            </p>
            <Link
              to="#!"
              className="site_btn cancel_subscription_btn"
              onClick={handleToggle}
            >
              Cancel Subscription
              <FormProcessingSpinner isFormProcessing={isFormProcessing} />
            </Link>
          </div>
        ) : (
          ""
        )}
      </div>

      {show ? (
        <div>
          <CustomPopup onCancel={handleToggle} />
        </div>
      ) : null}

      {showPause ? (
        <PausePopup
          onClose={togglePause}
          planEndDate={formatLongDate(subscription?.expiry_date)}
          defaultPauseFee={pauseFee}
        />
      ) : null}
    </>
  );
};

export default MembershipManagement;
